import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useJobPolling } from 'hooks/useJobPolling';
import cn from 'classnames';

import { ReactComponent as Arrow } from 'assets/arrow.svg';
import { ReactComponent as FigmaLogo } from 'assets/figma.svg';
import glyphWaves from 'assets/glyph_waves.webm';
import imageUploadIcon from 'assets/image_upload_icon.png';
import spinner from 'assets/remix-spinner.gif';
import removeFrameIcon from 'assets/remove_frame_icon.svg';
import videoUploadIcon from 'assets/video_upload_icon.png';

import { MAX_UPLOAD_FILES } from 'Pages/TeamDashboard/CreateReview/components/constants';

import { getFigmaConnected } from 'Redux/Actions/figmaActions';

import FigmaFile from 'services/figma_file';
import FilesService from 'services/files';
import JobsService from 'services/jobs';

import { errorReactToastify, infoReactToastify } from 'utlis/toasts';
import { isFigmaURL } from 'utlis/validators';

import styles from './styles.module.scss';

const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const ACCEPTED_VIDEO_EXTENSIONS = '.mp4,.mov,.webm';
const MAX_VIDEO_SIZE_MB = 100;

const VIDEO_PHASES = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  PROCESSING: 'processing',
  FAILED: 'failed',
};

const UploadScreens = ({
  uploadedImages,
  setUploadedImages,
  setOriginalFiles,
  figmaUrl,
  setFigmaUrl,
  setFigmaFramesData,
  onNext,
  onBack,
  authUser,
  userProfile,
  title,
  productDescription,
  audience,
  businessGoal,
  selectedCategory,
  selectedProblemChips,
  hideHeader,
  hideFooter,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);
  const currentRequestRef = useRef(null);
  const videoCancelledRef = useRef(false);
  // Maps uid → raw File object so we can keep originalFiles in sync when removing
  const uidToFileRef = useRef({});
  const previewRowRef = useRef(null);

  const [isPreviewScrollable, setIsPreviewScrollable] = useState(false);
  const [imageLoadedMap, setImageLoadedMap] = useState({});

  const [figmaError, setFigmaError] = useState('');
  const [isLoadingFigmaImages, setIsLoadingFigmaImages] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [videoPhase, setVideoPhase] = useState(VIDEO_PHASES.IDLE);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoJobId, setVideoJobId] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  const handleVideoJobCompleted = useCallback(outputPayload => {
    const mappedFrames = (outputPayload?.frames || []).map((frame, index) => ({
      name: `Frame ${index + 1}`,
      uid: `video-frame-${Date.now()}-${index}`,
      preview: frame.s3_url,
      url: frame.s3_url,
      progress: 100,
      isProgressLoading: false,
      isVideoFrame: true,
      timestamp: frame.timestamp_ms,
    }));

    setUploadedImages(mappedFrames);
    setVideoPhase(VIDEO_PHASES.IDLE);
    setVideoJobId(null);
    setVideoFile(null);
  }, [setUploadedImages]);

  const handleVideoJobFailed = useCallback(error => {
    setVideoPhase(VIDEO_PHASES.FAILED);
    errorReactToastify(
      error || 'Video processing failed. Please try again.',
      {
        autoClose: 5000,
        position: 'bottom-center',
        hideProgressBar: true,
        style: { backgroundColor: '#FDF0F0', width: '470px' },
      }
    );
  }, []);

  const { stopPolling: stopVideoPolling } = useJobPolling({
    jobId: videoJobId,
    interval: 3000,
    onCompleted: handleVideoJobCompleted,
    onFailed: handleVideoJobFailed,
  });

  // Detect if preview row is scrollable
  useEffect(() => {
    const el = previewRowRef.current;

    if (!el) {
      return;
    }

    const check = () => setIsPreviewScrollable(el.scrollWidth > el.clientWidth);

    // Delay to allow images to render and acquire their natural widths
    const timer = setTimeout(check, 50);

    const observer = new ResizeObserver(check);

    observer.observe(el);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [uploadedImages]);

  // Track per-image load state (for skeleton placeholders)
  useEffect(() => {
    setImageLoadedMap(prev => {
      const next = { ...prev };

      uploadedImages.forEach(img => {
        if (!(img.uid in next)) {
          next[img.uid] = false;
        }
      });

      Object.keys(next).forEach(uid => {
        if (!uploadedImages.find(img => String(img.uid) === uid)) {
          delete next[uid];
        }
      });

      return next;
    });
  }, [uploadedImages]);

  // Debounce figma URL fetch
  useEffect(() => {
    if (!figmaUrl || figmaError) {
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchFigmaImages(figmaUrl);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      currentRequestRef.current = null;
    };
  }, [figmaUrl, figmaError]);

  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();

    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);

    if (files.length === 0) {
      return;
    }

    const droppedVideos = files.filter(file => ACCEPTED_VIDEO_TYPES.includes(file.type));
    const droppedImages = files.filter(file => file.type.startsWith('image/'));

    if (droppedVideos.length > 0 && droppedImages.length > 0) {
      errorReactToastify('Please drop either video files or image files, not both.');

      return;
    }

    if (droppedVideos.length > 1) {
      errorReactToastify('Please drop only one video file at a time.');

      return;
    }

    if (droppedVideos.length === 1) {
      handleVideoUpload(droppedVideos[0]);

      return;
    }

    processFiles(files);
  };

  const isVideoActive = videoPhase !== VIDEO_PHASES.IDLE;
  const showUploadOptions = uploadedImages.length === 0 && !isLoadingFigmaImages && !isVideoActive;
  const showImagesUploaded = uploadedImages.length > 0 && !isLoadingFigmaImages && !isVideoActive;
  const isTooManyImages = uploadedImages.length > MAX_UPLOAD_FILES;
  const isNextDisabled = uploadedImages.length === 0
    || isTooManyImages
    || videoPhase === VIDEO_PHASES.UPLOADING
    || videoPhase === VIDEO_PHASES.PROCESSING;

  return (
    <div className={styles.container}>
      {!hideHeader && (
        <div className={styles.header}>
          <div className="flex items-center gap-4">
            <h1 className={styles.title}>
              Upload your screens
            </h1>
          </div>
        </div>
      )}

      <div className={styles.content}>
        <div
          className={[
            styles.dropZone,
            isDragging ? styles.dropZoneDragging : '',
            showImagesUploaded ? styles.dropZoneWithImages : '',
            (isLoadingFigmaImages
              || videoPhase === VIDEO_PHASES.UPLOADING
              || videoPhase === VIDEO_PHASES.PROCESSING)
              ? styles.dropZoneProcessing
              : '',
          ].join(' ')}
          onDragEnter={showUploadOptions ? handleDragEnter : undefined}
          onDragOver={showUploadOptions ? handleDragOver : undefined}
          onDragLeave={showUploadOptions ? handleDragLeave : undefined}
          onDrop={showUploadOptions ? handleDrop : undefined}
        >
          {/* Hidden image file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          {/* Hidden video file input */}
          <input
            ref={videoFileInputRef}
            type="file"
            accept={ACCEPTED_VIDEO_EXTENSIONS}
            onChange={handleVideoFileSelect}
            className="hidden"
          />

          {showUploadOptions && (
            <>
              <p className="text-sm text-[#606776]">
                <b>
                  Drag and drop
                </b>
                {' '}
                a screen recording or screenshots
              </p>

              <div className={styles.uploadCard}>
                <button type="button" onClick={triggerVideoFileInput} className={styles.uploadCardButton}>
                  <img src={videoUploadIcon} alt="" className="w-16 h-16" />
                  <span className="text-sm font-normal text-[#121723]">
                    Upload screen recording
                  </span>
                </button>
                <div className={styles.uploadCardDivider} />
                <button type="button" onClick={triggerFileInput} className={styles.uploadCardButton}>
                  <span className={styles.uploadCardMaxLabel}>
                    {`Max. ${MAX_UPLOAD_FILES}`}
                  </span>
                  <img src={imageUploadIcon} alt="" className="w-16 h-16" />
                  <span className="text-sm font-normal text-[#121723]">
                    or screenshots
                  </span>
                </button>
              </div>

              <span className="text-xs text-[#8F97A5]">
                OR
              </span>

              {userProfile?.appTokens?.figma
                ? (
                  <div className={styles.figmaInputWrapper}>
                    <div className={styles.figmaInput}>
                      <FigmaLogo className="flex-shrink-0" />
                      <div className={styles.figmaInputFieldWrapper}>
                        <input
                          type="text"
                          value={figmaUrl}
                          onChange={e => {
                            setFigmaUrl(e.target.value);

                            if (e.target.value && !isFigmaURL(e.target.value)) {
                              setFigmaError('Please enter valid figma url');
                            }
                            else {
                              setFigmaError('');
                            }
                          }}
                          className={styles.figmaInputField}
                        />
                        {!figmaUrl && (
                          <span className={styles.figmaInputPlaceholder} aria-hidden="true">
                            Figma
                            {' '}
                            <b>
                              section
                            </b>
                            {' '}
                            link
                          </span>
                        )}
                      </div>
                      {!figmaUrl && (
                        <span className={styles.figmaInputCaption} aria-hidden="true">
                          {`Max. ${MAX_UPLOAD_FILES} frames`}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-red-500 mt-1">
                      {figmaError && figmaError}
                    </p>
                  </div>
                )
                : (
                  <button
                    type="button"
                    onClick={handleFigmaConnect}
                    className={styles.figmaConnectButton}
                  >
                    <FigmaLogo className="w-5 h-5" />
                    Connect Figma account
                  </button>
                )}
            </>
          )}

          {isLoadingFigmaImages && (
            <div className="flex flex-col items-center gap-16">
              <img src={spinner} alt="Loading" className="w-[34px] h-[34px]" />
              <p className="text-sm text-[#606776]">
                Loading your designs...
              </p>
            </div>
          )}

          {(videoPhase === VIDEO_PHASES.PROCESSING || videoPhase === VIDEO_PHASES.UPLOADING) && (
            <div className="absolute inset-0 flex items-center justify-center rounded-2xl overflow-hidden">
              <video
                src={glyphWaves}
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
              />
              <p className="relative text-sm text-[#606776] py-24 px-40 bg-white">
                { videoPhase === VIDEO_PHASES.UPLOADING && 'Uploading your video...'}
                { videoPhase === VIDEO_PHASES.PROCESSING && 'Loading your designs...'}
              </p>
            </div>
          )}

          {videoPhase === VIDEO_PHASES.FAILED && (
            <div className="flex flex-col items-center gap-4">
              <p className="text-sm text-red-500">
                Processing failed
              </p>
              <button type="button" className={styles.retryButton} onClick={handleVideoRetry}>
                Try Again
              </button>
            </div>
          )}

          {showImagesUploaded && (
            <div className={styles.imagePreview}>
              <div className={styles.previewHeader}>
                <span className={cn(styles.previewCounter, { [styles.previewCounterWarning]: isTooManyImages })}>
                  <b>
                    {uploadedImages.length}
                  </b>
                  {' '}
                  out of
                  {' '}
                  <b>
                    {MAX_UPLOAD_FILES}
                  </b>
                </span>
                {isTooManyImages && (
                  <span className={styles.previewOverLimitMessage}>
                    Keep
                    {' '}
                    {MAX_UPLOAD_FILES}
                    {' '}
                    screens or fewer to continue.
                  </span>
                )}
              </div>
              <div className={cn(styles.previewRowWrapper, { [styles.previewRowScrollable]: isPreviewScrollable })}>
                <div ref={previewRowRef} className={styles.previewRow}>
                  {uploadedImages.map((image, index) => (
                    <div key={image.uid} className={styles.previewItem}>
                      <div className={styles.previewImageWrapper}>
                        {!imageLoadedMap[image.uid] && (
                          <div className={styles.previewImageSkeleton} />
                        )}
                        <img
                          src={image.preview}
                          alt={image.name || `Screen ${index + 1}`}
                          className={styles.previewImage}
                          onLoad={() => setImageLoadedMap(prev => ({ ...prev, [image.uid]: true }))}
                          style={imageLoadedMap[image.uid] ? undefined : { opacity: 0 }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(image.uid)}
                        className={styles.removeImageButton}
                        aria-label={`Remove screen ${index + 1}`}
                      >
                        <img src={removeFrameIcon} width={30} height={30} alt="Remove" />
                      </button>
                      <span className={styles.previewNumber}>
                        {index + 1}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end w-full mt-auto">
                <button type="button" onClick={clearSelection} className={styles.clearButton}>
                  Clear screens
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {!hideFooter && (
        <div className={styles.footer}>
          <div className={styles.buttons}>
            <button type="button" className={styles.button} onClick={onBack}>
              Back
            </button>
            <button
              type="button"
              className={cn(styles.button, {
                [styles.buttonDisabled]: isNextDisabled,
              })}
              onClick={onNext}
              disabled={isNextDisabled}
            >
              Add your context
              <Arrow />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  function processFiles(files) {
    if (uploadedImages.length + files.length > MAX_UPLOAD_FILES) {
      errorReactToastify(`You can only upload up to ${MAX_UPLOAD_FILES} images`, {
        autoClose: 5000,
        position: 'bottom-center',
        hideProgressBar: true,
        style: { backgroundColor: '#FDF0F0', width: '470px' },
      });

      return;
    }

    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      errorReactToastify('Please upload valid image files', {
        autoClose: 5000,
        position: 'bottom-center',
        hideProgressBar: true,
        style: { backgroundColor: '#FDF0F0', width: '470px' },
      });

      return;
    }

    const filesWithId = imageFiles.map(file => {
      const uid = Date.now() + Math.random();

      uidToFileRef.current[uid] = file;

      return {
        ...file,
        name: file.name,
        uid,
        // eslint-disable-next-line compat/compat
        preview: URL.createObjectURL(file),
        progress: 0,
        isProgressLoading: false,
        url: null,
      };
    });

    setUploadedImages(prev => [...prev, ...filesWithId]);
    setOriginalFiles(Object.values(uidToFileRef.current));
  }

  function removeImage(uid) {
    delete uidToFileRef.current[uid];
    setUploadedImages(prev => prev.filter(img => img.uid !== uid));
    setOriginalFiles(Object.values(uidToFileRef.current));
  }

  function handleFileSelect(event) {
    const files = Array.from(event.target.files);

    processFiles(files);
    event.target.value = '';
  }

  function triggerFileInput() {
    setFigmaError('');
    fileInputRef.current?.click();
  }

  function triggerVideoFileInput() {
    videoFileInputRef.current?.click();
  }

  function handleVideoFileSelect(event) {
    const file = event.target.files?.[0];

    if (file) {
      handleVideoUpload(file);
    }

    event.target.value = '';
  }

  async function handleVideoUpload(file) {
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      errorReactToastify('Please select a valid video file (MP4, MOV, or WEBM).');

      return;
    }

    if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
      errorReactToastify(`Video must be smaller than ${MAX_VIDEO_SIZE_MB}MB.`);

      return;
    }

    videoCancelledRef.current = false;
    setVideoFile(file);
    setVideoPhase(VIDEO_PHASES.UPLOADING);
    setVideoProgress(0);

    try {
      const videoUrl = await FilesService.uploadFileFromLocal(file, {
        onUploadProgress: ({ progress }) => {
          if (!videoCancelledRef.current) {
            setVideoProgress(Math.round(progress * 100));
          }
        },
      });

      if (videoCancelledRef.current) {
        return;
      }

      const jobResponse = await JobsService.createJob('video_frame_extraction', { video_url: videoUrl });

      if (videoCancelledRef.current) {
        return;
      }

      const newJobId = jobResponse.uuid || jobResponse.job_id;

      setVideoPhase(VIDEO_PHASES.PROCESSING);
      setVideoJobId(newJobId);
    }
    catch (err) {
      if (!videoCancelledRef.current) {
        setVideoPhase(VIDEO_PHASES.FAILED);
        errorReactToastify('Failed to upload video. Please try again.');
      }
    }
  }

  function handleVideoCancel() {
    videoCancelledRef.current = true;
    stopVideoPolling();
    setVideoPhase(VIDEO_PHASES.IDLE);
    setVideoJobId(null);
    setVideoFile(null);
    setVideoProgress(0);
  }

  function handleVideoRetry() {
    setVideoPhase(VIDEO_PHASES.IDLE);
    setVideoJobId(null);
    setVideoFile(null);
    setVideoProgress(0);
  }

  function clearSelection() {
    uidToFileRef.current = {};
    setUploadedImages([]);
    setOriginalFiles([]);
    setFigmaUrl('');
    setFigmaFramesData?.(null);
    infoReactToastify(
      'All your screens have been cleared',
      { position: 'bottom-center', style: { backgroundColor: '#EEF8FF', width: '470px' } }
    );
  }

  function handleFigmaConnect() {
    if (title && title.trim()) {
      localStorage.setItem('teamReview_title', title);
    }

    if (productDescription && productDescription.trim()) {
      localStorage.setItem('teamReview_productDescription', productDescription);
    }

    if (audience && audience.trim()) {
      localStorage.setItem('teamReview_audience', audience);
    }

    if (businessGoal && businessGoal.trim()) {
      localStorage.setItem('teamReview_businessGoal', businessGoal);
    }

    if (selectedCategory) {
      localStorage.setItem('teamReview_selectedCategory', selectedCategory);
    }

    if (selectedProblemChips && Array.isArray(selectedProblemChips) && selectedProblemChips.length > 0) {
      localStorage.setItem('teamReview_selectedProblemChips', JSON.stringify(selectedProblemChips));
    }

    // Determine the source flow based on context
    // If we have title prop, we're in CreateReview flow
    // If hideHeader is true, we're in ProductLibrary AddFlowView
    const oauthSource = title ? 'createReview' : 'productLibrary';

    localStorage.setItem('figma_oauth_source', oauthSource);

    infoReactToastify(
      'Hang tight, we\'re connecting to your Figma account',
      { position: 'bottom-center', style: { backgroundColor: '#EEF8FF', width: '470px' } }
    );

    setTimeout(
      () => dispatch(getFigmaConnected(authUser, null, () => {}, 'team')),
      2000
    );
  }

  async function fetchFigmaImages(url) {
    if (!isFigmaURL(url)) {
      return;
    }

    const requestId = Date.now();

    currentRequestRef.current = requestId;
    setIsLoadingFigmaImages(true);

    try {
      const response = await FigmaFile.getFigmaPageImages(url, null, authUser);

      if (currentRequestRef.current !== requestId) {
        return;
      }

      if (response.data && response.data.status === 'success') {
        const data = response.data;
        const { image_urls = [], frame_count = 0 } = data;
        let frameList = Array.isArray(data.frames) && data.frames.length > 0
          ? data.frames
          : image_urls.map((imageUrl, i) => ({
            frame_id: `0:${i}`,
            frame_name: `Frame ${i + 1}`,
            image_url: imageUrl,
          }));

        if (frameList.length < frame_count && image_urls.length > 0) {
          const lastUrl = image_urls[image_urls.length - 1];

          while (frameList.length < frame_count) {
            frameList = frameList.concat({
              frame_id: `0:${frameList.length}`,
              frame_name: `Frame ${frameList.length + 1}`,
              image_url: lastUrl,
            });
          }
        }

        if (frameList.length > 0) {
          const figmaImages = frameList.map((frame, index) => {
            const imageUrl = frame.image_url || frame.imageUrl;

            return {
              name: frame.frame_name || `Figma Image ${index + 1}`,
              uid: `figma-${Date.now()}-${index}`,
              preview: imageUrl,
              url: imageUrl,
              progress: 100,
              isProgressLoading: false,
              isFigmaImage: true,
            };
          });

          setUploadedImages(figmaImages);

          setFigmaFramesData?.({
            frames: frameList,
            section_name: data.section_name ?? '',
            file_key: data.file_key ?? '',
            figma_url: url,
          });
        }
      }
    }
    catch (err) {
      if (currentRequestRef.current === requestId) {
        errorReactToastify(
          err?.response?.data?.message || 'Failed to fetch Figma images',
          {
            autoClose: 5000,
            position: 'bottom-center',
            hideProgressBar: true,
            style: { backgroundColor: '#FDF0F0', width: '470px', height: '56px' },
          }
        );

        if (err?.response?.data?.message) {
          setFigmaUrl('');
        }
      }
    }
    finally {
      if (currentRequestRef.current === requestId) {
        setIsLoadingFigmaImages(false);
      }
    }
  }
};

UploadScreens.propTypes = {
  uploadedImages: PropTypes.array.isRequired,
  setUploadedImages: PropTypes.func.isRequired,
  setOriginalFiles: PropTypes.func.isRequired,
  figmaUrl: PropTypes.string.isRequired,
  setFigmaUrl: PropTypes.func.isRequired,
  setFigmaFramesData: PropTypes.func,
  onNext: PropTypes.func,
  onBack: PropTypes.func,
  authUser: PropTypes.object,
  userProfile: PropTypes.object,
  title: PropTypes.string,
  productDescription: PropTypes.string,
  audience: PropTypes.string,
  businessGoal: PropTypes.string,
  selectedCategory: PropTypes.string,
  selectedProblemChips: PropTypes.arrayOf(PropTypes.string),
  hideHeader: PropTypes.bool,
  hideFooter: PropTypes.bool,
};

export default UploadScreens;
